/**
 * Zugriff auf die Kalkulations-API. Im Dev leitet der Vite-Proxy `/kalk` an den
 * Dienst weiter und hängt den Schlüssel an – der Browser sieht ihn nie.
 */
import { useEffect, useRef, useState } from "react";

const BASIS = import.meta.env.VITE_KALK_URL ?? "/kalk";

export type KalkAntwort<T> = { gueltig: true; ergebnis: T } | { gueltig: false; fehler: string };

export type KalkulatorDetail<E = unknown> = {
  id: string;
  name: string;
  beschreibung: string;
  version: string;
  quelle: string;
  beispiel: E;
  vorlagen?: Record<string, unknown>;
  abweichungen: string[];
};

export async function kalkDetail<E>(id: string): Promise<KalkulatorDetail<E>> {
  const res = await fetch(`${BASIS}/api/kalkulatoren/${id}`);
  if (!res.ok) throw new Error(`Kalkulator ${id} nicht erreichbar (${res.status})`);
  return (await res.json()) as KalkulatorDetail<E>;
}

export async function berechne<T>(id: string, eingabe: unknown, signal?: AbortSignal): Promise<KalkAntwort<T>> {
  const res = await fetch(`${BASIS}/api/kalkulatoren/${id}/berechne`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(eingabe),
    signal,
  });
  if (res.status === 400) {
    const body = (await res.json()) as { error: string; details?: { errors?: string[]; properties?: Record<string, { errors: string[] }> } };
    const felder = Object.entries(body.details?.properties ?? {})
      .flatMap(([k, v]) => v.errors.map((e) => `${k}: ${e}`))
      .slice(0, 3);
    return { gueltig: false, fehler: felder.length ? felder.join(" · ") : body.error };
  }
  if (!res.ok) throw new Error(`Berechnung fehlgeschlagen (${res.status})`);
  return (await res.json()) as KalkAntwort<T>;
}

/**
 * Rechnet bei jeder Eingabe neu, ohne Verzögerung; veraltete Antworten werden
 * verworfen. Das letzte gültige Ergebnis bleibt sichtbar, bis das neue da ist –
 * so springt nichts auf leer zurück.
 */
export function useKalkulation<T>(id: string, eingabe: unknown, aktiv = true) {
  const [ergebnis, setErgebnis] = useState<T | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [offline, setOffline] = useState(false);
  const laufend = useRef<AbortController | null>(null);
  const schluessel = JSON.stringify(eingabe);

  useEffect(() => {
    if (!aktiv) return;
    laufend.current?.abort();
    const ac = new AbortController();
    laufend.current = ac;
    setLaedt(true);
    berechne<T>(id, JSON.parse(schluessel), ac.signal)
      .then((a) => {
        if (ac.signal.aborted) return;
        setOffline(false);
        if (a.gueltig) {
          setErgebnis(a.ergebnis);
          setFehler(null);
        } else {
          setFehler(a.fehler);
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        if (err instanceof TypeError) setOffline(true);
        else setFehler(err instanceof Error ? err.message : "Unbekannter Fehler");
      })
      .finally(() => {
        if (!ac.signal.aborted) setLaedt(false);
      });
    return () => ac.abort();
  }, [id, schluessel, aktiv]);

  return { ergebnis, fehler, laedt, offline };
}
